import { create } from 'zustand';
import { storage, STORAGE_KEYS } from '@utils/storage';
import type { ServiceZone, UserAddress } from '@/types';

export interface SelectedCity {
  id: string;
  name: string;
  slug: string;
}

interface CachedServiceZone {
  zone: ServiceZone;
  timestamp: number;
}

interface LocationState {
  activeAddress: {
    displayName: string;
    fullAddress: string;
    city: string;
    district: string;
    latitude: number;
    longitude: number;
    addressId?: string;
  } | null;
  serviceZone: ServiceZone | null;
  selectedCity: SelectedCity | null;
  setActiveAddress: (address: LocationState['activeAddress']) => void;
  setServiceZone: (zone: ServiceZone | null) => void;
  setSelectedCity: (city: SelectedCity | null) => void;
  loadFromStorage: () => void;
  clear: () => void;
}

export const useLocationStore = create<LocationState>((set, get) => ({
  activeAddress: null,
  serviceZone: null,
  selectedCity: null,

  setActiveAddress: (address) => {
    set({ activeAddress: address });
    
    // Cache active address
    if (address) {
      storage.set(STORAGE_KEYS.ACTIVE_DELIVERY_ADDRESS, address);
    } else {
      storage.remove(STORAGE_KEYS.ACTIVE_DELIVERY_ADDRESS);
    }
  },

  setServiceZone: (zone) => {
    set({ serviceZone: zone });
    if (zone) {
      const zoneData: CachedServiceZone = {
        zone,
        timestamp: Date.now(),
      };
      storage.set(STORAGE_KEYS.SERVICE_ZONE_DATA, zoneData);
      set({ selectedCity: null });
      storage.remove(STORAGE_KEYS.SELECTED_CITY);
    } else {
      storage.remove(STORAGE_KEYS.SERVICE_ZONE_DATA);
    }
  },

  setSelectedCity: (city) => {
    set({ selectedCity: city });
    if (city) {
      storage.set(STORAGE_KEYS.SELECTED_CITY, city);
      set({ serviceZone: null });
      storage.remove(STORAGE_KEYS.SERVICE_ZONE_DATA);
    } else {
      storage.remove(STORAGE_KEYS.SELECTED_CITY);
    }
  },

  loadFromStorage: () => {
    const cachedAddress = storage.get<LocationState['activeAddress']>(STORAGE_KEYS.ACTIVE_DELIVERY_ADDRESS);
    const cachedZone = storage.get<CachedServiceZone>(STORAGE_KEYS.SERVICE_ZONE_DATA);
    
    if (cachedAddress) {
      set({ activeAddress: cachedAddress });
    }
    
    if (cachedZone && cachedZone.zone) {
      const now = Date.now();
      const cacheAge = now - cachedZone.timestamp;
      const LOCATION_CACHE_TTL = 24 * 60 * 60 * 1000;
      if (cacheAge < LOCATION_CACHE_TTL) {
        set({ serviceZone: cachedZone.zone });
      } else {
        storage.remove(STORAGE_KEYS.SERVICE_ZONE_DATA);
      }
    }

    const cachedCity = storage.get<LocationState['selectedCity']>(STORAGE_KEYS.SELECTED_CITY);
    if (cachedCity?.id && cachedCity?.name) {
      set({ selectedCity: cachedCity });
    }
  },

  clear: () => {
    set({ activeAddress: null, serviceZone: null, selectedCity: null });
    storage.remove(STORAGE_KEYS.ACTIVE_DELIVERY_ADDRESS);
    storage.remove(STORAGE_KEYS.SERVICE_ZONE_DATA);
    storage.remove(STORAGE_KEYS.SELECTED_CITY);
  },
}));
